(() => {
  if (window.eluneMotionReady) return;
  window.eluneMotionReady = true;

  const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
  const smallScreen = window.matchMedia('(max-width: 760px)');
  const ease = 'cubic-bezier(.22, .61, .36, 1)';
  const seen = new WeakSet();
  const wired = new WeakSet();
  const effects = new Map();
  const revealSelectors = [
    '.section-head', '.card', '.steps > div', '.home-how-head', '.home-how-card',
    '.home-proof-head', '.home-proof-grid > figure', '.conversion-story > h2',
    '.detail-story', '.detail-process > h2', '.detail-close', '.detail-faq > h2',
    '.ep-section-head', '.ep-process > article', '.ep-piece', '.ep-craft-visual',
    '.ep-craft-copy', '.ep-cta', '.ep-manifesto', '.ep-story-close',
    '.craft-section-heading', '.craft-steps > article', '.craft-materials-intro',
    '.craft-material-list > div', '.craft-size-panel', '.craft-close',
    '.faq-group-heading', '.faq-contact', '.page-header', '.collection-hero',
    '.elune-bag .bag-note', '.ef-atelier-grid > div'
  ].join(',');
  let observer;
  let lastCount = Number(document.querySelector('[data-cart-count]')?.textContent || 0);

  const cancel = element => {
    const effect = effects.get(element);
    if (!effect) return;
    effects.delete(element);
    effect.cancel();
  };
  const animate = (element, frames, options = {}) => {
    if (!element || preference.matches || !element.animate || !element.isConnected ||
        element.closest('[hidden]') || !element.getClientRects().length) return;
    cancel(element);
    const effect = element.animate(frames, {
      duration: smallScreen.matches ? 200 : 240, easing: ease, fill: 'none', ...options
    });
    effects.set(element, effect);
    const cleanup = () => { if (effects.get(element) === effect) effects.delete(element); };
    effect.addEventListener('finish', cleanup, { once: true });
    effect.addEventListener('cancel', cleanup, { once: true });
  };
  const reveal = (element, instant = false) => {
    observer?.unobserve(element);
    element.style.setProperty('--elune-delay', instant ? '0ms' : element.style.getPropertyValue('--elune-delay'));
    if (instant) {
      const previous = element.style.transition;
      element.style.transition = 'none';
      element.dataset.eluneReveal = 'visible';
      requestAnimationFrame(() => { element.style.transition = previous; });
    } else element.dataset.eluneReveal = 'visible';
  };
  const showAll = () => {
    observer?.disconnect();
    document.querySelectorAll('[data-elune-reveal="pending"]').forEach(element => reveal(element, true));
    [...effects.keys()].forEach(cancel);
  };
  const enter = (element, distance = 4, options = {}) => animate(element, [
    { opacity: .72, translate: `0 ${distance}px` }, { opacity: 1, translate: '0 0' }
  ], options);

  const initializeReveals = root => {
    if (!('IntersectionObserver' in window) || preference.matches) return;
    observer ||= new IntersectionObserver(entries => {
      entries.forEach(entry => { if (entry.isIntersecting) reveal(entry.target); });
    }, { threshold: 0, rootMargin: '0px 0px -20px 0px' });
    root.querySelectorAll(revealSelectors).forEach(element => {
      if (seen.has(element) || element.closest('[hidden]') || !element.getClientRects().length ||
          element.parentElement.closest('[data-elune-reveal]')) return;
      seen.add(element);
      if (element.getBoundingClientRect().top < innerHeight + 32) {
        element.dataset.eluneReveal = 'visible';
        return;
      }
      const group = element.parentElement;
      const index = group.matches('.grid,.steps,.home-how-grid,.home-proof-grid,.ep-process,.ep-pieces,.craft-steps,.craft-size-grid')
        ? Array.from(group.children).indexOf(element) : 0;
      element.style.setProperty('--elune-delay', `${smallScreen.matches ? 0 : Math.min(index, 2) * 55}ms`);
      element.dataset.eluneReveal = 'pending';
      observer.observe(element);
    });
  };

  const initializeInteractions = root => {
    root.querySelectorAll('.gallery-main img').forEach(image => {
      if (wired.has(image) || !('MutationObserver' in window)) return;
      wired.add(image);
      let waiting = false;
      const fade = () => {
        if (!waiting || !image.complete || !image.naturalWidth) return;
        waiting = false;
        // The existing detail view owns transform/scale; only opacity changes here.
        animate(image, [{ opacity: .62 }, { opacity: 1 }], { duration: 230 });
      };
      image.addEventListener('load', fade);
      image.addEventListener('error', () => { waiting = false; });
      new MutationObserver(() => { waiting = true; requestAnimationFrame(fade); })
        .observe(image, { attributes: true, attributeFilter: ['src', 'srcset'] });
    });
    root.querySelectorAll('[data-custom-step]').forEach(panel => {
      if (wired.has(panel) || panel.closest('[data-figure-inline]') || !('MutationObserver' in window)) return;
      wired.add(panel);
      let wasHidden = panel.hidden;
      new MutationObserver(() => {
        if (wasHidden && !panel.hidden) enter(panel, 4, { duration: 210 });
        if (panel.hidden) cancel(panel);
        wasHidden = panel.hidden;
      }).observe(panel, { attributes: true, attributeFilter: ['hidden'] });
    });
  };

  const initialize = (root = document) => {
    initializeReveals(root);
    initializeInteractions(root);
  };
  const start = () => {
    initialize();
    if (preference.matches || scrollY > 40) return;
    const hero = document.querySelector('.home-hero-copy,.ep-hero-copy,.craft-hero-copy,.faq-intro,.pdp-info');
    if (!hero || hero.getBoundingClientRect().top >= innerHeight) return;
    const children = Array.from(hero.children).filter(child =>
      child.matches('.kicker,.craft-kicker,.faq-kicker,h1,.home-hero-intro,.ep-hero-intro,.craft-lead,.pdp-subtitle,.home-buy-row,.home-trust')
    );
    children.forEach((child, index) => enter(child, smallScreen.matches ? 4 : 7, {
      duration: smallScreen.matches ? 360 : 460, delay: Math.min(index, 4) * 35, fill: 'backwards'
    }));
  };

  document.addEventListener('focusin', event => {
    const pending = event.target.closest?.('[data-elune-reveal="pending"]');
    if (pending) reveal(pending, true);
  });
  document.addEventListener('toggle', event => {
    const disclosure = event.target;
    if (!disclosure.matches?.('details') || !disclosure.open) return;
    const panel = disclosure.querySelector(':scope > .shop-dropdown,:scope > .account-panel,:scope > .mobile-links');
    if (panel) { enter(panel, -4, { duration: 190 }); return; }
    if (!disclosure.matches('.faq details,.pdp-accordions details,.faq-item,.ep-faq details,.ep-occasion-details,.figure-optional,.more-styles')) return;
    Array.from(disclosure.children).filter(child => child.tagName !== 'SUMMARY').forEach(child =>
      animate(child, [{ opacity: .65 }, { opacity: 1 }], { duration: 200 })
    );
  }, true);
  document.addEventListener('elune:cart-updated', event => {
    const count = Number(event.detail?.count);
    if (!Number.isFinite(count) || count === lastCount) return;
    lastCount = count;
    document.querySelectorAll('[data-cart-count]').forEach(badge => animate(badge, [
      { transform: 'scale(1)' }, { transform: 'scale(1.12)' }, { transform: 'scale(1)' }
    ], { duration: 280 }));
  });
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  preference.addEventListener('change', () => {
    if (preference.matches) showAll();
    else initialize();
  });
  window.addEventListener('pagehide', showAll);
  window.addEventListener('pageshow', event => { if (event.persisted) { showAll(); initialize(); } });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
